// /apps/patient/src/components/navigation/BottomTabBar.tsx

import { Link, useLocation } from 'react-router-dom';
import { useFeatures } from '../../contexts/FeatureContext';
import { getNavigationItems, getTabBarItems } from '../../config/navigationConfig';
import { cn } from '../../lib/utils';

interface BottomTabBarProps {
  counts?: {
    unreadMessages?: number;
    upcomingAppointments?: number;
    activePrograms?: number;
  };
}

export function BottomTabBar({ counts = {} }: BottomTabBarProps) {
  const location = useLocation();
  const { features, isLoading, isExercisePlatform } = useFeatures();

  if (isLoading || !features) {
    return null;
  }

  const platform = isExercisePlatform ? 'exercise' : 'ehr';
  const allItems = getNavigationItems(features.enabled_features, counts, platform);
  const tabItems = getTabBarItems(allItems);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-30"
    >
      <div
        className="flex justify-around items-center h-20"  // Increased from h-16
        style={{
          paddingTop: '1rem',  // Add top padding
          paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))'  // Increase from 0.5rem
        }}
      >
        {tabItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center justify-center flex-1 h-full py-2 px-1 relative",
                "transition-colors duration-200",
                isActive
                  ? "text-primary"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              <div className="relative">
                <Icon className="h-6 w-6" />
                {/* Only show badge if count > 0 */}
                {item.badge && item.badge > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </div>
              <span className="text-xs mt-1">{item.label}</span>
              {isActive && (
                <div className="absolute -top-4 left-0 right-0 h-0.5 bg-primary" />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}